import type * as React from 'react'
import {useEffect, useRef} from 'react'

import type {ActionCreator} from '../actions/types'
import {useAppDispatch, useAppSelector} from '../hooks/react-redux'
import {useQueryParamState} from '../hooks/routes'
import type {State} from '../reducers/types'

type ParamProps<V = string> = {
  name: string
  action: ActionCreator<V>
  selector: (state: State) => V
  defaultValue?: V
  valueToString?: (value: V) => string
  stringToValue?: (string: string) => V
}
let isPresent = false
export function Param<V>({
  name,
  action,
  selector,
  defaultValue,
  valueToString = value => (value != null ? String(value) : ''),
  stringToValue,
}: ParamProps<V>) {
  const value = useAppSelector(selector)
  const stringValue = valueToString(value)
  const dispatch = useAppDispatch()
  const [param, setParam] = useQueryParamState(
    name,
    defaultValue != null ? valueToString(defaultValue) : '',
  )
  useEffect(() => {
    if (param !== stringValue) {
      dispatch(action(stringToValue ? stringToValue(param ?? '') : (param as V)))
    }
    // only react to param changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param])

  const isInitialState = useRef(true)
  useEffect(
    () => () => {
      isInitialState.current = true
    },
    [],
  )
  useEffect(() => {
    if (isInitialState.current) {
      isInitialState.current = false
      return
    }
    if (stringValue !== param) {
      setParam(stringValue)
    }
    // only react to value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringValue])

  return null
}

type Child = React.ReactElement<ParamProps<unknown>>
type Children = Children[] | Child | boolean | null
type Props = {
  children: Children
}

function QuerySync({children}: Props) {
  useEffect(() => {
    if (isPresent && process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Please use a single instance of QuerySync')
    }

    isPresent = true
    return () => {
      isPresent = false
    }
  }, [])

  return children
}

export default QuerySync
